import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import PersonCell from './PersonCell';
import { isPersonOption, personPicture, personOptionLabel } from './personOption';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

// #72 (UI düzeltme demeti T6) — "ince varyant" (TextInput.jsx'in AYNI
// gerekçesi): taban (ne `minimal` ne `small`) yükseklik 48px→40px, dolgu
// 14px 14px→10px 14px. `small`/`minimal` zaten daha önce vardı (bu görevden
// ÖNCE de kompakt bir üçüncü kademe sunuyorlardı) — DEĞİŞTİRİLMEDİ, taban
// artık onlara daha yakın ama hâlâ görsel olarak ayırt edilebilir.
const Trigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $minimal, $small }) => ($minimal ? '6px 0' : $small ? '5px 10px' : '10px 14px')};
  border: ${({ $minimal, $small }) => ($minimal ? 'none' : $small ? '1px solid' : '1.5px solid')};
  border-color: ${({ theme, $error, $open }) =>
    $error ? theme.colors.auxiliar.danger : $open ? theme.colors.primary.main : '#E0E4E8'};
  border-bottom: ${({ $minimal, $open, theme }) =>
    $minimal ? `1px solid ${$open ? theme.colors.primary.main : '#949494'}` : undefined};
  border-radius: ${({ $minimal, $small }) => ($minimal ? '0' : $small ? '6px' : '4px')};
  background: transparent;
  cursor: pointer;
  transition: border-color 0.2s;
  min-height: ${({ $minimal, $small }) => ($minimal || $small ? '32px' : '40px')};
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ $small }) => ($small ? '12px' : '13px')};
`;

const SelectedText = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ $small }) => ($small ? '12px' : '13px')};
  color: ${({ theme, $placeholder }) => ($placeholder ? '#A0A8B4' : theme.colors.text.primary)};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ClearBtn = styled.button`
  display: flex;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: #A0A8B4;
  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const Dropdown = styled.div`
  position: absolute;
  ${({ $direction }) => ($direction === 'top' ? 'bottom: calc(100% + 4px);' : 'top: calc(100% + 4px);')}
  left: 0;
  right: 0;
  background: #fff;
  border: 1.5px solid #E0E4E8;
  border-radius: ${({ theme }) => theme.metrics.radius.medium};
  box-shadow: 0 8px 24px rgba(0, 66, 66, 0.1);
  z-index: 500;
  max-height: 240px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SearchInput = styled.input`
  padding: 10px 14px;
  font-family: 'Nunito Sans', sans-serif;
  border: none;
  border-bottom: 1px solid #E8ECF0;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  outline: none;
  color: ${({ theme }) => theme.colors.text.primary};
  &::placeholder { color: #A0A8B4; }
`;

const OptionsList = styled.div`
  overflow-y: auto;
  max-height: 200px;
`;

const Option = styled.div`
  padding: 10px 14px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme, $disabled }) => ($disabled ? '#A0A8B4' : theme.colors.text.primary)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.1s;
  background: ${({ $selected }) => ($selected ? 'rgba(2, 145, 239, 0.06)' : 'transparent')};

  &:hover { background: ${({ $disabled }) => ($disabled ? 'transparent' : 'rgba(2, 145, 239, 0.08)')}; }
`;

const NoResults = styled.div`
  padding: 12px 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #A0A8B4;
  text-align: center;
`;

const Label = styled.label`
  position: absolute;
  left: 14px;
  top: -8px;
  font-size: 11px;
  color: ${({ theme, $open }) => ($open ? theme.colors.primary.main : '#A0A8B4')};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #fff;
  padding: 0 4px;
  pointer-events: none;
  z-index: 1;
`;

export default function GenericSelect({
  label,
  options = [],
  value,
  onChange,
  idKey = 'id',
  labelKey = 'name',
  renderOption,
  placeholder,
  error,
  required,
  direction = 'bottom',
  minimal = false,
  size = 'default',
  // PMF-03 — opsiyonel "temizle" (X) düğmesi. VARSAYILAN true: mevcut tüm
  // çağrı siteleri DEĞİŞMEZ. clearable={false} yalnız seçimin BOŞ BIRAKILAMAZ
  // olduğu filtreler içindir (ör. PM Dashboard'un dönem seçicisi — dönemsiz
  // panel tüm dönemleri toplayıp kiracının mevcudunu aşan bir sayı üretiyordu,
  // bkz. PerformanceDashboardController).
  clearable = true,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === '') return null;
    return options.find((opt) => {
      const optId = typeof opt === 'object' ? opt[idKey] : opt;
      return String(optId) === String(value);
    });
  }, [value, options, idKey]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const q = search.toLocaleLowerCase('tr-TR');
    return options.filter((opt) => {
      const text = typeof opt === 'object' ? opt[labelKey] : String(opt);
      return text?.toLocaleLowerCase('tr-TR').includes(q);
    });
  }, [options, search, labelKey]);

  const getLabel = (opt) => {
    if (renderOption) return renderOption(opt);
    // Kişi seçenekleri her yerde avatarlı — bkz. ui/personOption.js.
    if (isPersonOption(opt)) {
      return (
        <PersonCell
          name={opt[labelKey] || personOptionLabel(opt)}
          profilePicture={personPicture(opt)}
          variant="circle"
          size={size === 'small' ? 22 : 26}
        />
      );
    }
    return typeof opt === 'object' ? opt[labelKey] : String(opt);
  };

  const getId = (opt) => (typeof opt === 'object' ? opt[idKey] : opt);

  const handleSelect = (opt) => {
    onChange?.(getId(opt));
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  // #74 — options.find(...) döner `undefined` eşleşme yokken, `null` DEĞİL.
  // `!== null` bunu ayırt edemiyordu: undefined !== null === true, yani
  // hasValue YANLIŞLIKLA true oluyordu ve getLabel(undefined) tetiklenip
  // tetikleyici kutuda placeholder yerine literal "undefined" yazısı
  // görünüyordu. `!= null` (gevşek eşitlik) undefined'ı da null'ı da
  // birlikte yakalar — depoda kurulu deseni (bkz. PeriodRangePicker.js,
  // ats-applications/*) izler, eqeqeq engeli yok.
  const hasValue = selectedOption != null;
  const small = size === 'small';

  return (
    <Wrapper ref={ref}>
      {(hasValue || open) && label && (
        <Label $open={open}>
          {label}
          {required && <span style={{ color: '#E53935', marginLeft: 2 }}>*</span>}
        </Label>
      )}
      <Trigger $open={open} $error={!!error} $minimal={minimal} $small={small} onClick={() => setOpen(!open)}>
        {hasValue && !renderOption && isPersonOption(selectedOption) ? (
          // PersonCell bir <div> üretir; SelectedText (<span>) içine sarılamaz.
          <PersonCell
            name={selectedOption[labelKey] || personOptionLabel(selectedOption)}
            profilePicture={personPicture(selectedOption)}
            variant="circle"
            size={small ? 20 : 24}
            style={{ flex: 1, overflow: 'hidden', fontSize: small ? 12 : 13 }}
          />
        ) : (
          <SelectedText $placeholder={!hasValue} $small={small}>
            {hasValue ? getLabel(selectedOption) : (placeholder || label || '')}
          </SelectedText>
        )}
        <Actions>
          {hasValue && clearable && <ClearBtn onClick={handleClear}><X size={small ? 12 : 14} /></ClearBtn>}
          <ChevronDown size={small ? 14 : 16} color="#A0A8B4" />
        </Actions>
      </Trigger>
      {open && (
        <Dropdown $direction={direction}>
          {options.length > 5 && (
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`${t('common.search')}...`}
              autoFocus
            />
          )}
          <OptionsList>
            {filteredOptions.length === 0 ? (
              <NoResults>{t('common.noResults')}</NoResults>
            ) : (
              filteredOptions.map((opt) => {
                const optDisabled = typeof opt === 'object' && opt.disabled === true;
                return (
                  <Option
                    key={getId(opt)}
                    $selected={String(getId(opt)) === String(value)}
                    $disabled={optDisabled}
                    onClick={() => { if (!optDisabled) handleSelect(opt); }}
                  >
                    {getLabel(opt)}
                  </Option>
                );
              })
            )}
          </OptionsList>
        </Dropdown>
      )}
    </Wrapper>
  );
}
